import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { IconButtonComponent } from '../../common/button/components/icon-button/icon-button.component';
import { IconColorDirective } from '../../common/icon/directives/icon-color.directive';
import { IconComponent } from '../../common/icon/icon.component';
import { SpinnerComponent } from '../../common/spinner/spinner.component';
import { ComponentVariant, Variants } from '../../common/variant/variant';
import { RtTextInput } from '../../utils/rt-input';

@Component({
  selector: 'rt-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.less'],
  host: {
    '[class.disable-invalid-styles]': 'disableInvalidStyles',
    '[class.disabled]': 'disabled',
    '[class.invalid]': 'invalid',
    '[class.has-clear-button]': 'hasClearButton',
    '[style.width]': 'width',
    '[style.max-width]': 'maxWidth ? maxWidth : null',
    '[class.readonly]': 'readOnly',
    '[class.has-dropdown]': 'hasDropdownIndicator',
    '[class]': 'variant',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: RtTextInput,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => InputComponent),
    },
  ],
  standalone: true,
  imports: [NgIf, SpinnerComponent, IconColorDirective, IconComponent, IconButtonComponent],
})
export class InputComponent extends RtTextInput {
  public static readonly VARIANTS: Variants = ['rounded', ['rounded', 'plain']];
  @ComponentVariant(InputComponent.VARIANTS)
  @Input()
  public variant: string;
  @Input() public type: string = 'text';
  @Input() public placeholder: string;
  @Input() public maxLength: number;
  @Input() public tabIndex: number | undefined;
  @Input() public width: string;
  @Input() public maxWidth: string | null = null;
  @Input() public autoFocus: boolean = false;
  @Input() public hasDropdownIndicator: boolean = false;
  @Input() public invalid: boolean;
  @Input() public readOnly: boolean = false;
  @Input() public hasClearButton: boolean = false;
  @Input() public loading: boolean = false;
  @Input() public focusOnClear: boolean = true;
  @Input() public displayedValue?: string | null = null;
  @Input() public autocompleteName?: string;
  @Input() public disableInvalidStyles: boolean = false;

  @Output() public reset: EventEmitter<Event> = new EventEmitter();

  @ViewChild('clearButton', { static: false, read: ElementRef }) public clearButton: ElementRef;
  @ViewChild('input', { static: true }) public input: ElementRef<HTMLInputElement>;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value || changes.displayedValue) {
      this.setValue(this.value);
    }

    if (changes.disabled) {
      this.setDisabledState(this.disabled);
    }

    if (changes.autoFocus) {
      if (this.autoFocus) {
        this.setFocus();
      }
    }
  }

  public ngOnInit(): void {
    if (this.autoFocus) {
      this.setFocus();
    }
  }

  public getNativeInputElement(): HTMLInputElement {
    return this.input.nativeElement;
  }

  public setFocus(): void {
    if (this.input.nativeElement) {
      this.input.nativeElement.focus();
    }
  }

  public setBlur(): void {
    if (this.input.nativeElement) {
      this.input.nativeElement.blur();
    }
  }

  public setInputFocus(): void {
    if (this.input && this.focusOnClear) {
      this.setFocus();
    }
  }

  public isRemoveButtonVisible(): boolean {
    return !!this.value && this.hasClearButton && !this.loading;
  }

  public onRemoveButtonClick(event: Event): void {
    this.valueChange.emit('');
    this.reset.emit(event);
    this.setInputFocus();
  }

  public setValue(value: string | null): void {
    this.value = value;
    this.input.nativeElement.value = this.displayedValue ?? value ?? '';
    // @ts-ignore
    if (!this.cdRef['destroyed']) {
      this.cdRef.detectChanges();
    }
  }
}
